import { useState } from 'react'
import { Link } from 'react-router-dom'
import { forgotPassword } from '../services/registrationApi'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function ForgotPasswordPage() {
  const [email, setEmail]       = useState('')
  const [emailError, setEmailError] = useState('')
  const [isLoading, setIsLoading]   = useState(false)
  const [submitted, setSubmitted]   = useState(false)
  const [formError, setFormError]   = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !EMAIL_RE.test(email.trim())) {
      setEmailError('Please enter a valid email address.')
      return
    }
    setEmailError('')
    setIsLoading(true)
    setFormError('')

    try {
      await forgotPassword(email.trim())
      setSubmitted(true)
    } catch (err) {
      const message = err instanceof Error ? err.message : ''
      if (message === 'rate_limit_exceeded') {
        setFormError('Too many requests. Please wait a few minutes and try again.')
      } else {
        setFormError('Something went wrong. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-rd-bg font-dmSans relative overflow-hidden">
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px]"
        style={{ background: 'radial-gradient(ellipse, rgba(74,222,128,0.08) 0%, transparent 70%)' }}
      />

      <nav className="relative z-10 px-6 py-4 border-b border-white/[0.07]">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-rd-text no-underline hover:opacity-80 transition-opacity"
        >
          <img
            src={`${import.meta.env.BASE_URL}logo-icon.png`}
            alt="ReputeDesk"
            className="w-8 h-8 object-contain"
          />
          <span className="font-sora font-bold text-lg">ReputeDesk</span>
        </Link>
      </nav>

      <div className="relative z-10 flex justify-center px-4 py-10 sm:py-14">
        <div className="w-full max-w-md">

          <div className="text-center mb-7">
            <h1 className="font-sora font-bold text-2xl sm:text-[1.75rem] text-rd-text mb-2 tracking-tight">
              Reset your password
            </h1>
            <p className="text-rd-muted text-sm">
              Enter your email and we'll send you a reset link.
            </p>
          </div>

          <div className="bg-rd-surface border border-white/[0.07] rounded-2xl p-6 sm:p-8">

            {submitted ? (
              <div className="text-center py-2">
                <div className="mb-4 text-4xl">📬</div>
                <p className="text-rd-text font-medium mb-2">Check your inbox</p>
                <p className="text-rd-muted text-sm">
                  If <span className="text-rd-text">{email}</span> is registered, you'll receive
                  a reset link within a few minutes. The link expires in 1 hour.
                </p>
                <Link
                  to="/login"
                  className="mt-6 inline-block text-sm text-rd-accent hover:opacity-80 transition-opacity"
                >
                  Back to sign in
                </Link>
              </div>
            ) : (
              <>
                {formError && (
                  <div
                    role="alert"
                    className="mb-5 rounded-lg bg-danger-500/10 border border-danger-500/30 px-4 py-3 text-sm text-danger-500"
                  >
                    {formError}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-rd-muted mb-1.5">
                      Email address
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-required="true"
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? 'email-error' : undefined}
                      className={`reg-input${emailError ? ' !border-danger-500/60' : ''}`}
                      placeholder="jane@example.com"
                    />
                    {emailError && (
                      <p id="email-error" role="alert" className="mt-1.5 text-sm text-danger-500">
                        {emailError}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    aria-busy={isLoading}
                    className="reg-btn-primary mt-1"
                  >
                    {isLoading ? (
                      <>
                        <svg aria-hidden="true" className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          {!submitted && (
            <p className="text-center text-sm text-rd-muted mt-6">
              Remember your password?{' '}
              <Link to="/login" className="text-rd-accent hover:opacity-80 transition-opacity">
                Sign in
              </Link>
            </p>
          )}

        </div>
      </div>
    </div>
  )
}
